/**
 * El alta que ve el dueño, de punta a punta.
 *
 * Junta lo que produjeron las seis tasks del epic: la derivación del estado
 * (TASK-02), el pie que guarda antes de avanzar (TASK-03), el paso de canales
 * con las secciones compartidas (TASK-04, TASK-06) y la revisión con el botón
 * de activar (TASK-05).
 *
 * **Ninguna de las seis decía quién las ensambla.** Sin esta página los seis PRs
 * eran cimiento sin producto: un dueño que se registraba veía exactamente lo
 * mismo que antes.
 *
 * ## Dos reglas que la página sostiene
 *
 * - **El `tenantId` sale del token, no de la URL.** Si viniera de la ruta,
 *   cambiarlo a mano sería lo primero que alguien probaría y el aislamiento
 *   dependería sólo del backend.
 * - **El paso lo decide el servidor.** Nada se guarda en `localStorage`: era el
 *   bug que la TASK-02 arregló, y esta página es el lugar más fácil para
 *   reintroducirlo.
 */

import type { RespuestaDeOnboarding } from '@chatbot/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.tsx';
import { api } from '../../lib/api.ts';
import {
  type EstadoDerivado,
  type IdDePaso,
  cadaCuantoPreguntar,
  derivarEstado,
} from './EstadoDelAlta';
import { PasoDeConocimiento } from './PasoDeConocimiento';
import { PasosDelAlta } from './PasosDelAlta';
import { TuBotEstaListo } from './TuBotEstaListo';
import { textos } from './textos';

/** Los pasos en orden, para saber si el servidor va hacia atrás o hacia adelante. */
const ORDEN: IdDePaso[] = ['identidad', 'conocimiento', 'listo'];

export function PaginaDelAlta() {
  const { user } = useAuth();
  // De `custom:tenantId`, que es lo único fiable del token: `custom:tenantScope`
  // no lo setea nadie y sale vacío siempre (FOLLOWUP-18). Leerlo de ahí hacía
  // que un dueño con su negocio ya creado viera «tu cuenta todavía no tiene un
  // negocio asociado».
  const tenantId = user?.tenantId ?? undefined;

  // El nombre del negocio vive en la META del tenant, no en el token ni en el
  // estado del alta. Sin él la plantilla diría «Sobre ana@huellas.co», que es
  // el correo de la dueña y no su negocio.
  const { data: elTenant } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.tenants.get(tenantId as string),
    enabled: Boolean(tenantId),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['onboarding-status', tenantId],
    queryFn: () => api.tenants.getOnboardingStatus(tenantId as string),
    enabled: Boolean(tenantId),
    // Mientras el conocimiento se indexa, el estado cambia SOLO. Sin volver a
    // preguntar, el dueño terminaba la entrevista y el botón de salir en vivo
    // no aparecía nunca. Cuándo y cuándo no, en `cadaCuantoPreguntar`.
    refetchInterval: (q) => (q.state.data ? cadaCuantoPreguntar(q.state.data) : false),
  });

  const delServidor = data ? derivarEstado(data as RespuestaDeOnboarding) : null;

  /**
   * Dónde está el dueño, que no es lo mismo que qué falta (GKTIBO-155).
   *
   * `derivarEstado` dice la verdad sobre qué está completo, y eso no cambia.
   * Lo que cambia es quién decide la navegación: **hacia adelante avanza el
   * dueño**, tocando «Continuar».
   *
   * Sin esto, probar el bot te sacaba de la pantalla donde estabas probando:
   * el servidor marca «ya probó» apenas el bot contesta el primer mensaje, el
   * sondeo lo trae unos segundos después, el paso se completaba solo y
   * `PasoDeConocimiento` se desmontaba con el dueño escribiendo. Nunca llegaba
   * a leer la respuesta —que es la única pantalla donde se ve si el bot
   * contestó desde el conocimiento o de ningún lado (GKTIBO-73)—.
   *
   * El servidor sigue mandando en dos casos: la primera carga, y cuando algo
   * deja de estar completo. Quedarse en el cierre con el conocimiento caído
   * sería mentirle sobre lo que tiene.
   */
  const [dondeEstoy, setDondeEstoy] = useState<IdDePaso | null>(null);
  const pasoDelServidor = delServidor?.pasoActivo ?? null;
  useEffect(() => {
    if (!pasoDelServidor) return;
    setDondeEstoy((actual) => {
      if (actual === null) return pasoDelServidor;
      // Hacia atrás manda el servidor: algo dejó de estar completo.
      if (ORDEN.indexOf(pasoDelServidor) < ORDEN.indexOf(actual)) return pasoDelServidor;
      // Hacia adelante resiste SÓLO el paso de conocimiento, que es donde el
      // dueño está trabajando y el único con un botón para salir. `identidad`
      // es una pantalla inerte: bloquearla lo dejaba encerrado viendo «falta
      // el nombre» justo cuando el servidor decía que ya estaba (ronda 1, H1).
      return actual === 'conocimiento' ? actual : pasoDelServidor;
    });
  }, [pasoDelServidor]);

  if (!tenantId) {
    // No debería pasar. Si pasa, decir algo es mejor que una pantalla en blanco
    // — y muchísimo mejor que pedirle el estado a `undefined`.
    return (
      <main className="alta-paso" data-testid="alta-sin-tenant">
        <p>{textos.errores.sinNegocio}</p>
      </main>
    );
  }

  if (isLoading) {
    // Renderizar el paso 1 mientras llega la respuesta es la experiencia que
    // esta épica venía a eliminar: el dueño vería «conectá tu canal» un segundo
    // antes de que le digan que ya lo tiene.
    return (
      <main className="alta-paso" data-testid="alta-cargando">
        <p>{textos.cargando}</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="alta-paso" data-testid="alta-error">
        <p>{textos.errores.noSePudoLeer}</p>
      </main>
    );
  }

  const estado = data as RespuestaDeOnboarding;
  // `delServidor` no puede ser null acá: `data` ya pasó las guardas de arriba.
  const derivado = delServidor as EstadoDerivado;
  // El primer render tras recibir los datos todavía no pasó por el efecto.
  const visible = dondeEstoy ?? derivado.pasoActivo;

  /**
   * Un paso posterior al que el dueño está mirando no se tilda.
   *
   * El servidor puede tener todo completo mientras él sigue en el paso 2, y
   * el indicador decía «Paso 2 de 3» con el 3 ya tildado — que es el mismo
   * bug que `EstadoDelAlta` documenta como arreglado, entrando por la otra
   * puerta (ronda 1, H2).
   */
  const pasos = derivado.pasos.map((p) =>
    ORDEN.indexOf(p.id) > ORDEN.indexOf(visible) ? { ...p, completo: false } : p,
  );

  return (
    <main className="alta-pagina">
      {/* Arriba de todo: en qué paso va y cuántos faltan. */}
      <PasosDelAlta pasos={pasos} activo={visible} />

      {/* Conectar un canal ya NO es un paso del alta (GKTIBO-120): el dueño
          lo hacía antes de haber visto a su bot contestar una sola vez. Vive
          en `/configuracion/canales`, y acá se avisa una vez al final. */}
      {visible === 'identidad' ? (
        // Sin identidad no hay bot que enseñar ni cierre que mostrar. Antes
        // este caso caía en «Tu bot está listo» —con botón de activar si había
        // canal— porque el ternario sólo distinguía `conocimiento`.
        <section className="alta-paso" data-testid="alta-identidad">
          <h2 className="card-title">{textos.identidad.titulo}</h2>
          <p className="alta-paso-ayuda">
            {derivado.pasos.find((p) => p.id === 'identidad')?.falta ?? textos.identidad.ayuda}
          </p>
        </section>
      ) : visible === 'conocimiento' ? (
        <PasoDeConocimiento
          tenantId={tenantId}
          nombre={elTenant?.tenant?.name ?? tenantId}
          probado={estado.probado}
          falta={derivado.pasos.find((p) => p.id === 'conocimiento')?.falta}
          /**
           * Avanzar es SU decisión, y se mueve cuando el servidor está de
           * acuerdo (GKTIBO-155).
           *
           * Moverse antes de preguntar mostraba el cierre un instante y
           * rebotaba al paso 2 cuando el conocimiento todavía se estaba
           * indexando (ronda 1, H3). Así el momento lo elige él y la verdad
           * del progreso sigue siendo la del servidor.
           */
          onSeguir={async () => {
            const { data: recien } = await refetch();
            if (recien && derivarEstado(recien).pasoActivo === 'listo') setDondeEstoy('listo');
          }}
        />
      ) : (
        <TuBotEstaListo
          estado={estado}
          activar={async () => {
            await api.tenants.setStatus(tenantId, 'active');
            await refetch();
          }}
        />
      )}
    </main>
  );
}
